import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  FeatureFlags,
  InlineLoading,
  Loading,
  Modal,
  TreeNode,
  TreeView,
} from '@carbon/react';
import {
  Add,
  Document,
  Edit,
  Folder,
  WarningFilled,
} from '@carbon/icons-react';
import { AddComment } from '@carbon/react/icons';
import { useAtom, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';
import { isActivityFeedPanelOpenAtom } from 'components/activity-feed-panel';
import { FormattedCount } from 'components/formatted-count/formatted-count';
import { useEval3Page } from './context';
import {
  useCalcDetail,
  useEvalsTree,
  usePackageName,
  usePackageType,
  useSavedEvaluations,
  type WorkspaceWithEvals,
} from './hooks';
import { AddWorkspaceModal } from './add-workspace-modal';
import { editPackagePath, evalDisplayName } from './utils';
import { activityFeedModeAtom, scopeViewAtom } from './atoms';
import type { ScopeView } from './types';
import styles from './eval-workspace.module.scss';

/** The provisional draft eval's tree-node id (one client-only draft at a time). */
const DRAFT_NODE_ID = 'eval-draft';

/** The pinned "Saved Evaluations" folder's tree-node id. */
const SAVED_EVALS_NODE_ID = 'saved-evaluations';

/** Scope dropdown labels ↔ values (P3 ships My / All only). */
const SCOPE_OPTIONS = ['All Workspaces', 'My Workspaces'] as const;
type ScopeLabel = (typeof SCOPE_OPTIONS)[number];
const LABEL_TO_SCOPE: Record<ScopeLabel, ScopeView> = {
  'All Workspaces': 'all',
  'My Workspaces': 'mine',
};
const SCOPE_TO_LABEL: Record<ScopeView, ScopeLabel> = {
  all: 'All Workspaces',
  mine: 'My Workspaces',
};

/**
 * The left-pane Evaluations tree (mock §3): a header (title + package-wide totals +
 * Add), a My/All scope dropdown, and a Carbon `TreeView` of workspace folders → eval
 * leaves. All data + state come from `useEvalsTree`; selection routes through it (a
 * folder → the management view, an eval leaf → the route). A folder's calcs load
 * lazily on expand. Loading / error / empty states are scoped to the tree region
 * (never a full-page block).
 *
 * The header Add (+) button opens the AddWorkspaceModal (its open state is owned here);
 * on a successful create, onCreated selects the new workspace. It's the single
 * discoverable entry point — the zero-workspace empty state is just text, no CTA.
 *
 * @return {JSX.Element} The Evaluations tree region.
 */
export function EvalWorkspace() {
  const {
    workspaces,
    workspaceCount,
    evalCount,
    isLoading,
    error,
    refetch,
    expandedWorkspaceIds,
    toggleExpand,
    calcStatusByWorkspace,
    selectEval,
    selectWorkspace,
    selectSavedEvals,
    startDraftEval,
    selectDraft,
  } = useEvalsTree();
  const { packageId, calculationId, workspaceId, isDraft, isSavedEvalsView } =
    useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const history = useHistory();
  // The package the whole tree is scoped to — shown above the "Evals" title. Reuses
  // the map's package fetch (no extra request); empty until it resolves, so nothing
  // renders in its place (no placeholder box — the line just appears once loaded).
  const packageName = usePackageName(packageId);
  // Salesforce/legacy packages (non-empty `type`) can't be bootstrapped by the edit
  // wizard — it resolves to 'unsupported' and silently bounces to /packages. We guard
  // the Edit affordance below and explain instead of letting that bounce happen.
  const packageType = usePackageType(packageId);
  const isEditableInInsights = packageType === '';
  // The package's saved evals — the pinned folder's leaves + count. Fetched here (the tree owner)
  // so the folder + its browse view share the one `useSavedEvaluations` cache entry.
  const savedQuery = useSavedEvaluations(packageId);
  const savedCalcs = savedQuery.data?.calculations ?? [];
  // Collapsed by default (consistent with the workspace folders), but auto-expanded when the
  // viewed eval is saved so its highlighted leaf is visible — mirrors how a workspace folder opens
  // for the eval you're in. One-way (like that expand): the user can still collapse it; navigating
  // to another saved eval re-opens it.
  const [savedExpanded, setSavedExpanded] = useState(false);
  useEffect(() => {
    if (calculationId !== null && calcDetail?.saved) {
      setSavedExpanded(true);
    }
  }, [calculationId, calcDetail?.saved]);
  const [scope, setScope] = useAtom(scopeViewAtom);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [sfEditWarningOpen, setSfEditWarningOpen] = useState(false);
  const setActivityFeedMode = useSetAtom(activityFeedModeAtom);
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);
  const openPackageActivity = useCallback(() => {
    setActivityFeedMode('package');
    setActivityFeedOpen(true);
  }, [setActivityFeedMode, setActivityFeedOpen]);

  // The workspace a from-scratch draft attaches to (§11g "[workspace]"): the open folder
  // (the route's workspace — set both when browsing it and when viewing an eval under
  // it), else the most-recently-active (first) workspace. `undefined` when the package
  // has no workspaces — the modal disables "Add an eval". (Under "My Workspaces" scope
  // with a foreign folder open, the fallback is the first of MINE — a v1-acceptable edge;
  // see deferred-items.md.)
  const addEvalTarget = useMemo(() => {
    const ws =
      workspaceId !== null
        ? workspaces.find((candidate) => candidate.id === workspaceId)
        : workspaces[0];
    return ws ? { id: ws.id, name: ws.name } : undefined;
  }, [workspaces, workspaceId]);

  // The highlighted node: the active eval leaf (an eval route always carries its
  // workspace, so the eval wins), else — since a draft route (`eval/new`) also carries a
  // workspace — the provisional draft node, else the open workspace folder (browse view).
  let activeNodeId: string | undefined;
  if (calculationId !== null) {
    // A saved eval is excluded from its workspace folder and lives under Saved Evaluations, so
    // highlight its saved-eval leaf. Derive from the OPEN eval's own detail (`calcDetail.saved`)
    // rather than a saved-list membership test, which would race the `useSavedEvaluations` fetch.
    activeNodeId = calcDetail?.saved
      ? `saved-eval-${calculationId}`
      : `eval-${calculationId}`;
  } else if (isDraft) {
    activeNodeId = DRAFT_NODE_ID;
  } else if (isSavedEvalsView) {
    activeNodeId = SAVED_EVALS_NODE_ID;
  } else if (workspaceId !== null) {
    activeNodeId = `ws-${workspaceId}`;
  }

  // A folder's children: a caret placeholder while collapsed, else loading / error /
  // leaves. A from-scratch draft (when this is its workspace) renders FIRST as a
  // provisional leaf, independent of the BE calc list's load state (it's client-only).
  const renderFolderChildren = (ws: WorkspaceWithEvals) => {
    const hasDraft = isDraft && workspaceId === ws.id;

    // Collapsed: render a hidden placeholder so the expand caret shows for folders that
    // have evals (or a draft) — without fetching (calcs load on expand). Carbon hides a
    // node's children while it's collapsed, so this is never visible.
    if (!expandedWorkspaceIds.has(ws.id)) {
      // Gate on the UNSAVED count — saved evals move to the Saved Evaluations folder, so a
      // workspace whose only calcs are saved must not show an expand caret (P8 decision 4).
      return ws.calculationCount - ws.savedCount > 0 || hasDraft ? (
        <TreeNode id={`ws-${ws.id}-placeholder`} label="…" />
      ) : null;
    }

    // The provisional draft leaf — selected/highlighted via `activeNodeId`; selecting it
    // shows the (package-mode) draft work area. Null when this isn't the draft's folder.
    const draftNode = hasDraft ? (
      <TreeNode
        key={DRAFT_NODE_ID}
        id={DRAFT_NODE_ID}
        label="New eval (draft)"
        renderIcon={Document}
        className={styles.draftLeaf}
        onSelect={() => selectDraft(ws.id)}
      />
    ) : null;

    const status = calcStatusByWorkspace[ws.id]?.status;
    if (status === 'loading') {
      return [
        draftNode,
        <TreeNode
          key="loading"
          id={`ws-${ws.id}-loading`}
          label={<InlineLoading description="Loading evals…" />}
        />,
      ].filter(Boolean);
    }
    if (status === 'error') {
      return [
        draftNode,
        <TreeNode
          key="error"
          id={`ws-${ws.id}-error`}
          renderIcon={WarningFilled}
          label={
            <span className={styles.calcError}>
              Couldn’t load evals.
              <Button
                kind="ghost"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  calcStatusByWorkspace[ws.id]?.onRetry();
                }}
              >
                Retry
              </Button>
            </span>
          }
        />,
      ].filter(Boolean);
    }
    if (ws.evals.length === 0) {
      // A draft makes the folder non-empty; otherwise the "No evaluations" leaf.
      return (
        draftNode ?? (
          <TreeNode
            id={`ws-${ws.id}-empty`}
            label="No evaluations"
            className={styles.emptyLeaf}
          />
        )
      );
    }
    return [
      draftNode,
      ...ws.evals.map((evaluation) => (
        <TreeNode
          key={evaluation.id}
          id={`eval-${evaluation.id}`}
          label={evalDisplayName(
            evaluation.id,
            evaluation.name,
            evaluation.createdBy
          )}
          renderIcon={Document}
          onSelect={() => selectEval(ws.id, evaluation.id)}
        />
      )),
    ].filter(Boolean);
  };

  // The pinned Saved Evaluations folder's children — its saved-eval leaves (each routes to its
  // eval via its OWN workspaceId), or a loading / error / empty leaf.
  const renderSavedChildren = () => {
    if (savedQuery.isPending) {
      return (
        <TreeNode
          id="saved-evaluations-loading"
          label={<InlineLoading description="Loading saved evals…" />}
        />
      );
    }
    if (savedQuery.isError) {
      return (
        <TreeNode
          id="saved-evaluations-error"
          renderIcon={WarningFilled}
          label="Couldn’t load saved evals."
        />
      );
    }
    if (savedCalcs.length === 0) {
      return (
        <TreeNode
          id="saved-evaluations-empty"
          label="No saved evaluations"
          className={styles.emptyLeaf}
        />
      );
    }
    return savedCalcs.map((calc) => (
      <TreeNode
        key={calc.id}
        id={`saved-eval-${calc.id}`}
        label={evalDisplayName(calc.id, calc.description, calc.runByUser?.name)}
        renderIcon={Document}
        onSelect={() => selectEval(calc.workspaceId, calc.id)}
      />
    ));
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.statusBlock}>
          <Loading small withOverlay={false} />
          <span className={styles.statusText}>Loading workspaces…</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className={styles.statusBlock}>
          <Button
            kind="danger--tertiary"
            size="sm"
            renderIcon={WarningFilled}
            onClick={refetch}
          >
            Retry
          </Button>
          <span className={styles.statusText}>Couldn’t load workspaces.</span>
        </div>
      );
    }
    // Truly no workspaces in the package. No CTA here — the header Add (+) button is
    // the single discoverable entry point.
    if (workspaceCount === 0) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>No workspaces yet.</p>
        </div>
      );
    }
    // Workspaces exist but the scope filtered them all out (My with none of mine).
    if (workspaces.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>No workspaces created by you.</p>
        </div>
      );
    }
    return (
      // enableTreeviewControllable makes Carbon's TreeView honor the `active` /
      // `selected` props reactively (and `isExpanded` via `onToggle`) — without it the
      // highlight is a mount-only seed, so programmatic selection (create / auto-
      // redirect) wouldn't highlight. Our per-node onSelect / onToggle still fire.
      <FeatureFlags enableTreeviewControllable>
        <TreeView
          label="Evaluations"
          hideLabel
          active={activeNodeId}
          selected={activeNodeId ? [activeNodeId] : []}
        >
          {/* Pinned Saved Evaluations folder (mock §3) — package-wide saved evals, above the
            workspace folders. Selecting it opens the saved-evals browse view; each leaf routes
            to its eval. */}
          <TreeNode
            id={SAVED_EVALS_NODE_ID}
            label={
              savedCalcs.length > 0 ? (
                <>
                  Saved Evaluations (
                  <FormattedCount value={savedCalcs.length} />)
                </>
              ) : (
                'Saved Evaluations'
              )
            }
            renderIcon={Folder}
            isExpanded={savedExpanded}
            onToggle={() => setSavedExpanded((prev) => !prev)}
            onSelect={selectSavedEvals}
          >
            {renderSavedChildren()}
          </TreeNode>
          {workspaces.map((ws) => (
            <TreeNode
              key={ws.id}
              id={`ws-${ws.id}`}
              label={ws.name}
              renderIcon={Folder}
              isExpanded={expandedWorkspaceIds.has(ws.id)}
              onToggle={() => toggleExpand(ws.id)}
              onSelect={() => selectWorkspace(ws.id)}
            >
              {renderFolderChildren(ws)}
            </TreeNode>
          ))}
        </TreeView>
      </FeatureFlags>
    );
  };

  return (
    <section className={styles.evalWorkspace} aria-label="Evaluations">
      <div className={styles.header}>
        {packageName && (
          <div className={styles.packageRow}>
            <p className={styles.packageName} title={packageName}>
              {packageName}
            </p>
            {/* Restores the "edit package" affordance the old Eval 3 page had
                (INS-11395). Client-side, same-tab navigation to the edit wizard
                via history.push — matches the legacy package-show affordance. */}
            <Button
              className={styles.editPackage}
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit package"
              tooltipPosition="bottom"
              onClick={() => {
                if (isEditableInInsights) {
                  history.push(editPackagePath(packageId));
                } else {
                  setSfEditWarningOpen(true);
                }
              }}
            />
            <Button
              hasIconOnly
              kind="tertiary"
              size="sm"
              renderIcon={AddComment}
              iconDescription="Package Activity"
              tooltipPosition="right"
              onClick={openPackageActivity}
            />
          </div>
        )}
        <div className={styles.titleRow}>
          <h2 className={styles.title}>Evals</h2>
          <Button
            kind="primary"
            size="sm"
            hasIconOnly
            renderIcon={Add}
            iconDescription="Add workspace"
            // Opens leftward (into the pane) — the button sits at the pane's right
            // edge, so a bottom/centered tooltip would spill past it into the work
            // area and clip.
            tooltipPosition="left"
            onClick={() => setAddModalOpen(true)}
          />
        </div>
        {!isLoading && !error && (
          <p className={styles.subtitle}>
            <FormattedCount value={workspaceCount} /> Workspaces |{' '}
            <FormattedCount value={evalCount} /> Evaluations
          </p>
        )}
      </div>

      <Dropdown
        id="eval-workspace-scope"
        size="sm"
        titleText="View"
        label=""
        items={[...SCOPE_OPTIONS]}
        selectedItem={SCOPE_TO_LABEL[scope]}
        className={styles.scopeDropdown}
        onChange={({ selectedItem }: { selectedItem: ScopeLabel | null }) => {
          if (selectedItem) {
            setScope(LABEL_TO_SCOPE[selectedItem]);
          }
        }}
      />

      <div className={styles.treeRegion}>{renderBody()}</div>

      {/* onCreated selects the new workspace's management view; it does NOT clear an
          in-progress draft — a draft persists across workspace selection/creation
          (only navigating to a real :evalId discards it, via E0b), consistent with the
          folder round-trip rule. A dedicated draft-discard affordance is a deferred
          fast-follow (see deferred-items.md). */}
      <AddWorkspaceModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={selectWorkspace}
        addEvalTarget={addEvalTarget}
        onAddEval={startDraftEval}
      />
      {/* Salesforce/legacy packages can't be edited in the Insights wizard. Rather
          than let the wizard silently bounce to /packages, explain why and keep the
          user on the Evals page (INS-11395; SF case flagged by Andrew Kock). */}
      <Modal
        open={sfEditWarningOpen}
        modalHeading="Can't edit this package in Insights"
        primaryButtonText="OK"
        shouldSubmitOnEnter
        onRequestSubmit={() => setSfEditWarningOpen(false)}
        onRequestClose={() => setSfEditWarningOpen(false)}
      >
        <p>
          This package was created in Salesforce and can&rsquo;t be edited in
          Insights. To make changes, clone it in Insights and edit the copy.
        </p>
      </Modal>
    </section>
  );
}
